/*
 * Kalmix custom code
 * File: assets/kmx-home-receive.js
 * Version: 1008.2
 * History:
 * - 1008.2 Minor labels hide only when the label column would overlap the model body
 *   (the cable, which runs off-frame, no longer counts), so desktop keeps them.
 * - 1008.1 three.js and the GLB load only when the act comes within about a screen
 *   of the viewport (the poster shows until then), so the first screen loads
 *   lighter. After 6 s without interaction the model turns slowly (until the next
 *   drag). On narrower stages the model takes less width and minor labels hide
 *   when the label column would overlap the model (around 1024px).
 * - 1006.8 Readout bars scaled to 9 px max (cap height of the 11 px mono digits).
 * - 1006.7 Top-right readout reduced to two lines, a quiet easter egg: L1 and L5 each
 *   get six tiny bars and one mean C/N0 (top-4 mean, L5 a few dB lower), status line
 *   below; hovering opens a small card with each signal. B2I logic removed. Signal
 *   relay is three lines dropping straight above the model. Label spacing 32 px.
 * - 1006.6 Tracking panel (top right): simulated C/N0 per signal, L1 row (GPS L1C/A,
 *   GAL E1, BDS B1I, BDS B1C, GLO L1) and L5 row (GPS L5, GAL E5a, BDS B2a, BDS B2I,
 *   QZSS L5 — not in view at Four Corners). Values drift a little every ~0.5 s;
 *   L5 sits a few dB below L1. A signal pulse reaching the antenna lights one
 *   cell. B2I shows only for the model named in data-b2i. Model switch marks the
 *   module card the receiver is built on. Label leads are a little darker.
 * - 1006.5 Layered explode: parts move one after another (top shell, antenna, PCB,
 *   shield can, base + cable) with ease-in-out; the intro is slow (1 s hold, 1.6 s
 *   per layer, 0.25 s apart), the button is quicker (1.1 s, 0.12 s apart) and
 *   assembling runs in reverse order. Each label fades in as its own part moves
 *   out. Timing is clock-based. Knowledge dots are always visible and sit in two
 *   fixed open spots left of the model (not tied to a part).
 * - 1006.4 Toggle keeps its icon (only the label text changes) and the section gets
 *   is-touched on the first click or drag, which stops the button's breathing
 *   halo. Model switch moves the segmented thumb (data-on). Knowledge dots sit in
 *   a column just left of the model at their part's height.
 * - 1006.3 Camera framing: keeps the recommended view direction from explode.json
 *   but fits the fully exploded bounding sphere (cable excluded) into the left ~70% of the stage
 *   (labels use the rest), so the model fills any stage ratio.
 * - 1006.2 Contact shadow follows the model: follows the lowest part as it explodes
 *   and is scaled to the shell footprint (the cable is left out) (Codex GLB 1006 is smaller than the placeholder).
 * - 1006.1 Initial delivery of 02 RECEIVE (phase 1). Interactive three.js exploded
 *   view of SCOUT: top shell, ceramic patch antenna, PCB, closed shield can, bottom
 *   shell, cable (GLB from Blender; procedural placeholder until a GLB URL is set).
 *   Auto-explodes once on enter, drag to rotate (limited), Explode / Assemble toggle.
 *   Labels follow the 3D anchors; the shield-can label names the core of the
 *   selected model. Signal relay: lines drop from the section top into the antenna.
 *   Model switch swaps the product panel. Mobile and reduced motion use the static
 *   exploded poster. Loads three.js r170 from jsDelivr (ES modules).
 */
const THREE_URL = 'https://cdn.jsdelivr.net/npm/three@0.170.0/+esm';
const GLTF_URL = 'https://cdn.jsdelivr.net/npm/three@0.170.0/examples/jsm/loaders/GLTFLoader.js/+esm';
const ROOM_URL = 'https://cdn.jsdelivr.net/npm/three@0.170.0/examples/jsm/environments/RoomEnvironment.js/+esm';

const PARTS = ['top_shell', 'antenna', 'pcb', 'shield_can', 'bottom_shell', 'cable'];
const DEFAULT_META = {
  units: 'm',
  explode: { top_shell: [0, .045, 0], antenna: [0, .024, 0], pcb: [0, .004, 0], shield_can: [0, -.016, 0], bottom_shell: [0, -.038, 0], cable: [0, -.038, 0] },
  anchors: { top_shell: [.03, .006, .03], antenna: [0, .004, 0], pcb: [.032, 0, .032], shield_can: [.02, -.003, .02], bottom_shell: [.032, -.004, .032], cable: [-.045, 0, 0] },
  camera: { fov: 30, position: [.19, .17, .26], target: [0, 0, 0] }
};

function ease(t) { return t < .5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2; }

async function initReceive(sec) {
  if (sec.__kmxInit) return; sec.__kmxInit = true;
  const KMX = window.KMX || { reduce: false, onVisible() {}, onEnter() {} };
  const reduce = KMX.reduce;
  const stage = sec.querySelector('.kmx-receive__stage');
  const glCanvas = sec.querySelector('.kmx-receive__gl');
  const overlay = sec.querySelector('.kmx-receive__overlay');
  const relay = sec.querySelector('.kmx-receive__relay');
  const poster = sec.querySelector('.kmx-receive__poster');
  const toggleBtn = sec.querySelector('.kmx-receive__toggle');
  const labels = {};
  sec.querySelectorAll('.kmx-receive__label').forEach((el) => { labels[el.dataset.part] = el; });
  let meta = DEFAULT_META;
  try { const m = JSON.parse(sec.getAttribute('data-explode') || ''); if (m && m.explode) meta = Object.assign({}, DEFAULT_META, m); } catch (e) { /* use default */ }
  const glbUrl = sec.getAttribute('data-glb') || '';
  const mq = window.matchMedia('(max-width: 860px)');

  /* ---------- 型号切换（与 3D 无关，先绑定） ---------- */
  const shieldText = labels.shield_can ? labels.shield_can.querySelector('b') : null;
  const seg = sec.querySelector('.kmx-seg');
  function setModel(k) {
    if (seg) seg.dataset.on = k;
    rxModel(k);
    sec.querySelectorAll('.kmx-receive__part[data-inside]').forEach((c) => c.classList.toggle('is-inside', c.dataset.inside === k));
    sec.querySelectorAll('.kmx-seg__btn').forEach((b) => { const on = b.dataset.model === k; b.classList.toggle('is-on', on); b.setAttribute('aria-selected', on); });
    sec.querySelectorAll('.kmx-receive__model').forEach((m) => { m.hidden = m.dataset.model !== k; });
    const core = sec.querySelector('.kmx-receive__model[data-model="' + k + '"]');
    if (shieldText && core) shieldText.textContent = core.getAttribute('data-shield') || '';
    flash = 1;
  }
  let flash = 0;

  /* ---------- 右上角彩蛋：L1 / L5 两组小柱 + 平均 C/N0（示意值）；悬停才展开各频点 ---------- */
  const rxp = sec.querySelector('.kmx-rx');
  const BANDS = { L1: 43, L5: 39 }; // 开阔天空的典型量级，L5 比 L1 低几个 dB
  const SIGS = [
    { band: 'L1', id: 'GPS L1C/A', c: 44 }, { band: 'L1', id: 'GAL E1', c: 43 }, { band: 'L1', id: 'BDS B1I', c: 42 },
    { band: 'L1', id: 'BDS B1C', c: 39 }, { band: 'L1', id: 'GLO L1', c: 41 },
    { band: 'L5', id: 'GPS L5', c: 41 }, { band: 'L5', id: 'GAL E5a', c: 40 }, { band: 'L5', id: 'BDS B2a', c: 38 },
    { band: 'L5', id: 'QZSS L5', c: 0 }
  ];
  const groups = {};
  if (rxp) {
    const line = rxp.querySelector('.kmx-rx__line'), card = rxp.querySelector('.kmx-rx__card');
    Object.keys(BANDS).forEach((band) => {
      const gEl = document.createElement('span'); gEl.className = 'kmx-rx__grp';
      gEl.innerHTML = '<span class="kmx-rx__b">' + band + '</span><span class="kmx-rx__bars"></span><b>--</b>';
      const bars = []; const box = gEl.querySelector('.kmx-rx__bars');
      for (let i = 0; i < 6; i++) { const it = document.createElement('i'); box.appendChild(it); bars.push({ el: it, c: BANDS[band] + Math.round((Math.random() - .5) * 10), v: 0 }); }
      bars.forEach((b) => { b.v = b.c; });
      groups[band] = { bars, out: gEl.querySelector('b') };
      line.appendChild(gEl);
    });
    const u = document.createElement('span'); u.className = 'kmx-rx__u'; u.textContent = 'dB-Hz'; line.appendChild(u);
    if (card) card.innerHTML = SIGS.map((g) => '<span' + (g.c ? '' : ' class="is-off"') + '>' + g.id + '<b>' + (g.c || '—') + '</b></span>').join('');
  }
  function rxDraw() {
    Object.keys(groups).forEach((band) => {
      const g = groups[band]; let sum = 0;
      g.bars.forEach((b) => { b.el.style.height = Math.max(2, Math.min(9, (b.v - 25) / 25 * 9)) + 'px'; sum += b.v; });
      const top = g.bars.map((b) => b.v).sort((a, b2) => b2 - a).slice(0, 4);
      g.out.textContent = Math.round(top.reduce((a, v) => a + v, 0) / top.length);
    });
  }
  function rxTick() {
    Object.keys(groups).forEach((band) => groups[band].bars.forEach((b) => {
      b.v = Math.max(b.c - 3, Math.min(b.c + 3, b.v + Math.round((Math.random() - .5) * 2.2)));
    }));
    rxDraw();
  }
  function rxHit() {
    const all = [].concat(...Object.keys(groups).map((k) => groups[k].bars)); if (!all.length) return;
    const b = all[Math.floor(Math.random() * all.length)];
    b.el.classList.add('is-hit'); setTimeout(() => b.el.classList.remove('is-hit'), 380);
  }
  function rxModel() {}
  rxDraw();
  // 约每 0.5 秒小幅跳动；只在本屏可见时更新，"减少动态"时保持静止
  if (rxp && !reduce) setInterval(() => { if (!KMX.isVisible || KMX.isVisible(sec)) rxTick(); }, 520);
  sec.querySelectorAll('.kmx-seg__btn').forEach((b) => b.addEventListener('click', () => setModel(b.dataset.model)));
  setModel(sec.getAttribute('data-default') || 'pro');

  // 手机与"减少动态"：只用拆开状态的静帧
  if ((mq.matches || reduce) && poster) { sec.classList.add('is-static'); return; }

  // 延后加载：本幕接近视口（约一屏内）时才加载 three.js 与 GLB，之前显示静帧
  await new Promise((res) => {
    if (!window.IntersectionObserver) { res(); return; }
    const io = new IntersectionObserver((es) => { if (es.some((e) => e.isIntersecting)) { io.disconnect(); res(); } }, { rootMargin: '120% 0px' });
    io.observe(sec);
  });

  let THREE, GLTFLoader, RoomEnvironment;
  try {
    THREE = await import(THREE_URL);
    ({ GLTFLoader } = await import(GLTF_URL));
    ({ RoomEnvironment } = await import(ROOM_URL));
  } catch (err) { if (poster) sec.classList.add('is-static'); return; }

  const renderer = new THREE.WebGLRenderer({ canvas: glCanvas, antialias: true, alpha: true });
  renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
  renderer.outputColorSpace = THREE.SRGBColorSpace;
  renderer.toneMapping = THREE.ACESFilmicToneMapping;
  renderer.toneMappingExposure = 1.05;
  const scene = new THREE.Scene();
  const pmrem = new THREE.PMREMGenerator(renderer);
  scene.environment = pmrem.fromScene(new RoomEnvironment(), .04).texture;
  const key = new THREE.DirectionalLight(0xffffff, 1.6); key.position.set(.3, .6, .4); scene.add(key);
  const rim = new THREE.DirectionalLight(0xbfe3ff, .6); rim.position.set(-.4, .2, -.3); scene.add(rim);
  const cam = new THREE.PerspectiveCamera(meta.camera.fov || 30, 1, .001, 10);
  const target = new THREE.Vector3().fromArray(meta.camera.target || [0, 0, 0]);
  const camPos0 = new THREE.Vector3().fromArray(meta.camera.position || [.19, .17, .26]);
  const pivot = new THREE.Group(); scene.add(pivot);

  // 接触阴影：一张柔和的圆形渐变
  const sh = document.createElement('canvas'); sh.width = sh.height = 128;
  const shx = sh.getContext('2d'); const g = shx.createRadialGradient(64, 64, 4, 64, 64, 64);
  g.addColorStop(0, 'rgba(15,28,36,.35)'); g.addColorStop(1, 'rgba(15,28,36,0)'); shx.fillStyle = g; shx.fillRect(0, 0, 128, 128);
  const shadow = new THREE.Mesh(new THREE.PlaneGeometry(.16, .16), new THREE.MeshBasicMaterial({ map: new THREE.CanvasTexture(sh), transparent: true, depthWrite: false }));
  shadow.rotation.x = -Math.PI / 2; shadow.position.y = -.065; scene.add(shadow);

  /* ---------- 模型：GLB 或程序化占位 ---------- */
  const nodes = {};
  function placeholder() {
    const mat = (c, r, m) => new THREE.MeshStandardMaterial({ color: c, roughness: r, metalness: m });
    const plastic = mat(0x1b1f22, .78, 0);
    const box = (w, h, d, m, y) => { const o = new THREE.Mesh(new THREE.BoxGeometry(w, h, d), m); o.position.y = y; return o; };
    const grp = (name, ...meshes) => { const gg = new THREE.Group(); gg.name = name; meshes.forEach((x) => gg.add(x)); pivot.add(gg); nodes[name] = gg; };
    grp('top_shell', box(.07, .009, .07, plastic, .009));
    grp('antenna', box(.044, .005, .044, mat(0xd9b4a6, .45, 0), .006), box(.03, .0012, .03, mat(0xd8dde2, .25, .9), .0092), box(.05, .007, .05, mat(0xbfc4c8, .35, .8), .0035));
    grp('pcb', box(.064, .0016, .064, mat(0x1e7a4f, .5, .05), 0));
    grp('shield_can', box(.04, .005, .04, mat(0xc9ced2, .3, .85), -.004));
    grp('bottom_shell', box(.07, .008, .07, plastic, -.008));
    const cab = new THREE.Mesh(new THREE.CylinderGeometry(.0022, .0022, .07, 16), mat(0x101214, .6, 0));
    cab.rotation.z = Math.PI / 2; cab.position.set(-.07, -.004, .02); grp('cable', cab);
  }
  async function loadGlb(url) {
    const gltf = await new GLTFLoader().loadAsync(url);
    const root = gltf.scene; pivot.add(root);
    PARTS.forEach((n) => { const o = root.getObjectByName(n); if (o) nodes[n] = o; });
    if (!nodes.antenna) throw new Error('GLB missing nodes');
  }
  try { if (glbUrl) await loadGlb(glbUrl); else placeholder(); } catch (e) { placeholder(); }
  const base = {}; PARTS.forEach((n) => { if (nodes[n]) base[n] = nodes[n].position.clone(); });
  const off = {}; PARTS.forEach((n) => { off[n] = new THREE.Vector3().fromArray(meta.explode[n] || [0, 0, 0]); });
  const anchorsLocal = {}; PARTS.forEach((n) => { anchorsLocal[n] = new THREE.Vector3().fromArray((meta.anchors || {})[n] || [0, 0, 0]); });
  // 天线发光：找到天线上的材质，收到信号时轻微发蓝光
  const glowMats = []; if (nodes.antenna) nodes.antenna.traverse((o) => { if (o.material && 'emissive' in o.material) { o.material = o.material.clone(); glowMats.push(o.material); } });
  const shieldMats = []; if (nodes.shield_can) nodes.shield_can.traverse((o) => { if (o.material && 'emissive' in o.material) { o.material = o.material.clone(); shieldMats.push(o.material); } });
  // 阴影贴在拆开后最低零件的下方，大小随模型
  { const bb = new THREE.Box3().setFromObject(pivot);
    const low = Math.min(0, ...PARTS.map((n) => off[n].y));
    shadow.position.set((bb.min.x + bb.max.x) / 2, bb.min.y - .004, (bb.min.z + bb.max.z) / 2);
    shadow.userData = { y0: bb.min.y - .004, low };
    const tb = new THREE.Box3().setFromObject(nodes.top_shell || pivot), ts = tb.getSize(new THREE.Vector3());
    shadow.position.x = (tb.min.x + tb.max.x) / 2; shadow.position.z = (tb.min.z + tb.max.z) / 2;
    shadow.scale.setScalar(Math.max(ts.x, ts.z) * 1.5 / .16); }
  if (poster) poster.classList.add('is-hidden');

  /* ---------- 尺寸 ---------- */
  const tmp = new THREE.Vector3();
  let W = 1, H = 1;
  // 相机适配：沿推荐视角方向，把"完全拆开"的包围球放进舞台左侧约 70%，右侧留给标注
  const fitCenter = new THREE.Vector3(); let fitR = .08;
  const fitBox = new THREE.Box3();
  { const bb = fitBox;
    PARTS.forEach((n) => { if (!nodes[n]) return; nodes[n].position.copy(base[n]).add(off[n]); });
    PARTS.forEach((n) => { if (nodes[n] && n !== 'cable') bb.expandByObject(nodes[n]); }); // 线缆可以出画，不参与取景
    PARTS.forEach((n) => { if (nodes[n]) nodes[n].position.copy(base[n]); });
    const sph = bb.getBoundingSphere(new THREE.Sphere()); fitCenter.copy(sph.center); fitR = sph.radius; }
  const viewDir = camPos0.clone().sub(target).normalize();
  function fitCamera() {
    const narrow = W < 600, share = narrow ? 1 : (W < 820 ? .58 : .7);
    const vHalf = THREE.MathUtils.degToRad(cam.fov / 2);
    const hHalf = Math.atan(Math.tan(vHalf) * cam.aspect * share);
    const dist = fitR * 1.02 / Math.sin(Math.min(vHalf, hHalf));
    camPos0.copy(fitCenter).addScaledVector(viewDir, dist); target.copy(fitCenter);
    if (narrow) cam.clearViewOffset(); else cam.setViewOffset(W, H, W * (1 - share) / 2, 0, W, H);
    cam.updateProjectionMatrix();
    // 知识点：放在模型左侧两处空位（按完全拆开状态算一次，不随零件移动）
    cam.position.copy(camPos0); cam.lookAt(target); cam.updateMatrixWorld();
    let l = W, t = H, b = 0;
    for (let i = 0; i < 8; i++) {
      tmp.set(i & 1 ? fitBox.max.x : fitBox.min.x, i & 2 ? fitBox.max.y : fitBox.min.y, i & 4 ? fitBox.max.z : fitBox.min.z).project(cam);
      const x = (tmp.x + 1) / 2 * W, y = (1 - tmp.y) / 2 * H; l = Math.min(l, x); t = Math.min(t, y); b = Math.max(b, y);
    }
    const spots = [[l - 44, t + (b - t) * .3], [l - 12, t + (b - t) * .74]];
    sec.querySelectorAll('.kmx-receive__stage .kmx-kdot').forEach((k, i) => {
      const sp = spots[i % spots.length]; k.style.left = Math.max(18, sp[0]) + 'px'; k.style.top = sp[1] + 'px';
    });
  }
  function resize() {
    const r = stage.getBoundingClientRect(); W = r.width; H = r.height;
    renderer.setSize(W, H, false); cam.aspect = W / H; fitCamera();
    [overlay, relay].forEach((c) => { const d = Math.min(window.devicePixelRatio || 1, 2); const rr = c.getBoundingClientRect(); c.width = rr.width * d; c.height = rr.height * d; c.getContext('2d').setTransform(d, 0, 0, d, 0, 0); });
  }
  resize(); window.addEventListener('resize', resize);

  /* ---------- 交互：拖动旋转（小角度）+ 拆开 / 合上 ---------- */
  let yaw = 0, pitch = 0, dragging = false, px = 0, py = 0, idle = 0;
  stage.addEventListener('pointerdown', (e) => { if (e.pointerType !== 'mouse' || e.target.closest('a,button')) return; dragging = true; sec.classList.add('is-touched'); px = e.clientX; py = e.clientY; stage.setPointerCapture(e.pointerId); stage.classList.add('is-drag'); });
  stage.addEventListener('pointermove', (e) => {
    if (!dragging) return;
    yaw = Math.max(-.7, Math.min(.7, yaw + (e.clientX - px) * .006)); pitch = Math.max(-.25, Math.min(.3, pitch + (e.clientY - py) * .004));
    px = e.clientX; py = e.clientY; idle = 0;
  });
  const endDrag = () => { dragging = false; stage.classList.remove('is-drag'); };
  stage.addEventListener('pointerup', endDrag); stage.addEventListener('pointercancel', endDrag);
  // 分层拆开：每个零件按顺序移动，各自缓入缓出；合上时倒序
  const ORDER = ['top_shell', 'antenna', 'pcb', 'shield_can', 'bottom_shell'];
  const rank = { top_shell: 0, antenna: 1, pcb: 2, shield_can: 3, bottom_shell: 4, cable: 4 };
  const cur = {}, from = {}; PARTS.forEach((n) => { cur[n] = 0; from[n] = 0; });
  let eTarget = 0, started = false, tw = null;
  function tween(v, slow) {
    const now = performance.now();
    PARTS.forEach((n) => { from[n] = cur[n]; });
    tw = { to: v, t0: now + (slow ? 1000 : 0), dur: slow ? 1600 : 1100, gap: slow ? 250 : 120 };
  }
  function stepTween(now) {
    if (!tw) return;
    let done = true;
    PARTS.forEach((n) => {
      const r = tw.to ? rank[n] : ORDER.length - 1 - rank[n];
      const k = Math.min(1, Math.max(0, (now - tw.t0 - r * tw.gap) / tw.dur));
      if (k < 1) done = false;
      cur[n] = from[n] + (tw.to - from[n]) * ease(k);
    });
    if (done) tw = null;
  }
  const tLabel = toggleBtn ? toggleBtn.querySelector('.kmx-receive__tlabel') || toggleBtn : null;
  function setExplode(v, slow) { eTarget = v; tween(v, slow); if (toggleBtn) { tLabel.textContent = v ? toggleBtn.dataset.assemble : toggleBtn.dataset.explode; toggleBtn.setAttribute('aria-pressed', v ? 'true' : 'false'); } }
  if (toggleBtn) toggleBtn.addEventListener('click', () => { sec.classList.add('is-touched'); idle = 0; setExplode(eTarget ? 0 : 1); });
  KMX.onEnter(sec, () => { if (started) return; started = true; setExplode(1, true); });

  /* ---------- 信号接力：从版块顶部落入天线 ---------- */
  const rx = relay.getContext('2d');
  const drops = [-150, -30, 100].map((f, i) => ({ f, k: -i * .3 }));
  function project(v) { tmp.copy(v).project(cam); return { x: (tmp.x + 1) / 2 * W, y: (1 - tmp.y) / 2 * H }; }

  let running = false, lt = 0;
  function frame(t) {
    if (!running) return;
    const dt = Math.min(64, t - (lt || t)); lt = t;
    // 拆开进度（按时间，逐层）
    stepTween(performance.now());
    PARTS.forEach((n) => { if (nodes[n]) nodes[n].position.copy(base[n]).addScaledVector(off[n], cur[n]); });
    if (shadow.userData.y0 !== undefined) shadow.position.y = shadow.userData.y0 + shadow.userData.low * cur.bottom_shell;
    // 视角：拖动 + 空闲时极轻微摆动；6 秒无操作后缓慢转动
    idle += dt; const spin = idle > 6000 ? Math.sin((idle - 6000) / 5200) * .35 * Math.min(1, (idle - 6000) / 2500) : 0;
    const sway = dragging ? 0 : Math.sin(idle / 2600) * .05 + spin;
    pivot.rotation.set(pitch, yaw + sway, 0);
    cam.position.copy(camPos0); cam.lookAt(target);
    // 信号命中天线时发光；切换型号时屏蔽罩轻闪
    flash = Math.max(0, flash - dt / 900);
    const gl = Math.max(0, glow - dt / 700); glow = gl;
    glowMats.forEach((m) => m.emissive.setRGB(.15 * gl, .35 * gl, .55 * gl));
    shieldMats.forEach((m) => m.emissive.setRGB(.1 * flash, .25 * flash, .4 * flash));
    renderer.render(scene, cam);
    drawOverlay(); drawRelay(dt);
    requestAnimationFrame(frame);
  }
  let glow = 0;

  /* ---------- 标注：引线 + HTML 文字，跟随 3D 锚点 ---------- */
  const ox = overlay.getContext('2d');
  const box = new THREE.Box3();
  const bodyBox = new THREE.Box3();
  function anchorScreen(n) {
    if (!nodes[n]) return null;
    tmp.copy(anchorsLocal[n]); nodes[n].localToWorld(tmp); tmp.project(cam);
    return { x: (tmp.x + 1) / 2 * W, y: (1 - tmp.y) / 2 * H };
  }
  function drawOverlay() {
    ox.clearRect(0, 0, W, H);
    // 标注列：放在模型投影包围盒右侧，且整列不超出舞台
    box.setFromObject(pivot); let bx = 0;
    for (let i = 0; i < 8; i++) { tmp.set(i & 1 ? box.max.x : box.min.x, i & 2 ? box.max.y : box.min.y, i & 4 ? box.max.z : box.min.z).project(cam); bx = Math.max(bx, (tmp.x + 1) / 2 * W); }
    let lw = 0; PARTS.forEach((n) => { if (labels[n]) lw = Math.max(lw, labels[n].offsetWidth); });
    const col = Math.min(bx + 24, W - lw - 22);
    // 只按模型本体（不含伸出画面的线缆）判断标注列是否压到模型
    bodyBox.makeEmpty(); PARTS.forEach((n) => { if (nodes[n] && n !== 'cable') bodyBox.expandByObject(nodes[n]); });
    let bxBody = 0; for (let i = 0; i < 8; i++) { tmp.set(i & 1 ? bodyBox.max.x : bodyBox.min.x, i & 2 ? bodyBox.max.y : bodyBox.min.y, i & 4 ? bodyBox.max.z : bodyBox.min.z).project(cam); bxBody = Math.max(bxBody, (tmp.x + 1) / 2 * W); }
    const tight = bxBody + 16 > W - lw - 22; // 标注列放不下时隐藏次要标注及其引线
    const pts = PARTS.filter((n) => labels[n]).map((n) => ({ n, a: anchorScreen(n) })).filter((p) => p.a).sort((p, q) => p.a.y - q.a.y);
    let lastY = -1e9;
    pts.forEach((p) => {
      const el = labels[p.n], vis = Math.min(1, Math.max(0, (cur[p.n] - .45) / .4));
      if (tight && el.classList.contains('is-minor')) { el.style.opacity = 0; return; }
      let y = Math.max(p.a.y, lastY + 32); lastY = y;
      el.style.opacity = vis * (el.classList.contains('is-minor') ? .55 : 1);
      el.style.transform = 'translate(' + (col + 14) + 'px,' + (y - 8) + 'px)';
      if (vis <= 0) return;
      ox.globalAlpha = vis * (el.classList.contains('is-minor') ? .5 : 1);
      ox.strokeStyle = '#7f8d94'; ox.lineWidth = 1; ox.setLineDash([2, 3]);
      ox.beginPath(); ox.moveTo(p.a.x, p.a.y); ox.lineTo(col - 12, y); ox.lineTo(col + 6, y); ox.stroke(); ox.setLineDash([]);
      ox.fillStyle = '#0056b3'; ox.beginPath(); ox.arc(p.a.x, p.a.y, 2.6, 0, 7); ox.fill();
      ox.globalAlpha = 1;
    });
  }
  function drawRelay(dt) {
    const rr = relay.getBoundingClientRect(), sr = stage.getBoundingClientRect();
    const w = rr.width, h = rr.height; rx.clearRect(0, 0, w, h);
    const a = anchorScreen('antenna'); if (!a) return;
    const tx = a.x + sr.left - rr.left, ty = a.y + sr.top - rr.top;
    drops.forEach((d) => {
      const sx = tx + d.f, sy = 0; // 只从模型正上方落下
      const lg = rx.createLinearGradient(sx, sy, tx, ty); lg.addColorStop(0, 'rgba(124,200,255,.0)'); lg.addColorStop(1, 'rgba(0,86,179,.22)');
      rx.strokeStyle = lg; rx.lineWidth = .8; rx.beginPath(); rx.moveTo(sx, sy); rx.lineTo(tx, ty); rx.stroke();
      d.k += dt / 2600; if (d.k > 1) { d.k -= 1.15; glow = 1; rxHit(); }
      if (d.k > 0) {
        const k0 = Math.max(0, d.k - .08), ax = sx + (tx - sx) * d.k, ay = sy + (ty - sy) * d.k, bx = sx + (tx - sx) * k0, by = sy + (ty - sy) * k0;
        const pg = rx.createLinearGradient(bx, by, ax, ay); pg.addColorStop(0, 'rgba(0,86,179,0)'); pg.addColorStop(1, 'rgba(0,86,179,.75)');
        rx.strokeStyle = pg; rx.lineWidth = 1.4; rx.beginPath(); rx.moveTo(bx, by); rx.lineTo(ax, ay); rx.stroke();
      }
    });
  }

  function start() { if (running) return; running = true; lt = 0; requestAnimationFrame(frame); }
  function stop() { running = false; }
  KMX.onVisible(sec, (v) => (v ? start() : stop()));
  if (!window.IntersectionObserver) start();
}

function boot() { document.querySelectorAll('.kmx-receive').forEach(initReceive); }
if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot); else boot();
document.addEventListener('shopify:section:load', (ev) => { const s = ev.target.querySelector('.kmx-receive'); if (s) initReceive(s); });
